/**
 * Multi-Revision AI Modal Component
 * Shows 3-5 AI-generated revisions in tabs for comparison
 */

import React, { useState } from 'react';
import { X, Copy, Check, Sparkles, Zap, Users, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

export function MultiRevisionModal({ 
  revisions, 
  onClose, 
  onInsert,
  totalWords,
  numRevisions,
  subscriptionTier,
  powerCreditUsed,
  documentId,
  isOwner = true,
  onCollaborativeReview
}) {
  const [selectedRevision, setSelectedRevision] = useState(0);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleInsert = () => {
    onInsert(revisions[selectedRevision].text);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">AI Multi-Revision Results</h2>
              <p className="text-sm text-gray-600">
                {numRevisions} versions • {totalWords.toLocaleString()} words total
                {powerCreditUsed && (
                  <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                    <Zap className="w-3 h-3 inline mr-1" />
                    Power Credit Used
                  </span>
                )}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 p-4 border-b overflow-x-auto">
          {revisions.map((revision, index) => (
            <button
              key={index}
              onClick={() => setSelectedRevision(index)}
              className={`px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors ${
                selectedRevision === index
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Version {revision.revision_number}
              <span className="ml-2 text-xs opacity-75">
                ({revision.words} words)
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="prose max-w-none">
            <p className="whitespace-pre-wrap text-gray-800 leading-relaxed">
              {revisions[selectedRevision].text}
            </p>
          </div>

          {revisions[selectedRevision].error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                ⚠️ Generation Error: {revisions[selectedRevision].error}
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            Model: <span className="font-mono text-xs">{revisions[selectedRevision].model}</span>
          </div>
          <div className="flex gap-3">
            {/* Collaborative Review Button (if owner and has collaborators) */}
            {isOwner && onCollaborativeReview && (
              <Button
                variant="outline"
                onClick={() => onCollaborativeReview(revisions)}
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
              >
                <Users className="w-4 h-4 mr-2" />
                Get Team Feedback
              </Button>
            )}
            
            <Button
              variant="outline"
              onClick={() => handleCopy(revisions[selectedRevision].text, selectedRevision)}
            >
              {copiedIndex === selectedRevision ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy to Clipboard
                </>
              )}
            </Button>
            
            <Button onClick={handleInsert} className="bg-purple-600 hover:bg-purple-700">
              Insert into Document
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
